/**
 * The waiting room: the code, who is in it, and the one button that starts the game.
 *
 * It is opened knowing what it is for. Hosting and joining are chosen on the menu, where a mode can
 * be named, so this screen never asks; it connects on the way up and shows what happened.
 *
 * It shows exactly what a player can act on. The code, big enough to read out loud and one tap to
 * copy. Who is connected, how well, and whether they are ready. One sentence about what the
 * connection is doing, written by `sessionErrors`, so this file never invents wording for a
 * failure. Relay attempts, candidate types and channel state belong in the diagnostics panel.
 *
 * Ready is advisory. The host can always start, and starting with somebody not ready says so once
 * and asks again rather than refusing: a lobby that one idle player can hold shut is worse than a
 * game that starts a moment early.
 *
 * The dice sits above the player list, for the same reason it sits on the menu: a name is rolled,
 * never typed, and rolling one here renames the seat rather than waiting for the next session.
 *
 * The host starts the game and everybody else is taken into it: a guest never presses anything,
 * because the host owns the world and hands out the player ids, so a guest starting itself would be
 * a guest deciding something that is not its to decide.
 *
 * The lobby's transport becomes the game's transport rather than being closed and reopened. That is
 * what `relayTo` on the session is for, and it is why leaving is suppressed once a game has taken
 * the connection over.
 */

import {
  LOBBY_BACK,
  LOBBY_DISCONNECTED,
  LOBBY_NOT_READY,
  LOBBY_READY,
  LOBBY_RULES_LABEL,
  LOBBY_RULES_OFF,
  LOBBY_RULES_ON,
  LOBBY_START,
  LOBBY_STRAP,
  LOBBY_TITLE,
  lobbyNotReadyWarning,
} from '@/text/lobbyText';
import { attachNetPanel } from '@/dev/netPanelHost';
import { createLobbyCodePanel } from '@/ui/lobby/lobbyCodePanel';
import { createNameRoller } from '@/ui/identity/nameRoller';
import { createLobbyPlayerList } from '@/ui/lobby/lobbyPlayerList';
import { createLobbyReconnectPanel } from '@/ui/lobby/lobbyReconnectPanel';
import { createLobbySession } from '@/net/lobby/lobbySession';
import { decodeStart } from '@/net/wire/startFrame';
import { el, setText, setVisible } from '@/ui/dom';
import { HOST_PLAYER_ID } from '@/net/session/sessionLimits';
import { injectLobbyStyles } from '@/ui/lobby/lobbyStyles';
import { MESSAGE_KINDS } from '@/net/wire/messageKinds';
import { netErrorAdvice, netErrorMessage } from '@/net/session/sessionErrors';
import { sessionFromLobby } from '@/net/session/netSession';
import { silentListeners } from '@/net/transportContract';
import type { CharacterSheet } from '@/sim/progression/characterSheet';
import type { ExitReason } from '@/modes/modeContract';
import type { LobbyIntent } from '@/app/appShell';
import type { LobbySession } from '@/net/lobby/lobbySession';
import type { LobbyState } from '@/net/lobby/lobbyState';
import type { RosterSeat } from '@/net/wire/lobbyFrame';
import type { SessionNotice } from '@/net/lobby/lobbyNotices';
import type { LobbyGameFacts, NetSession } from '@/net/session/netSession';

export interface CoopLobbyHandlers {
  /** What this screen was opened to do. Absent leaves it a connection screen and nothing more. */
  readonly intent?: LobbyIntent | null;
  readonly onExit: (reason?: ExitReason) => void;
  /** What this machine is called. The shell keeps it, because this screen comes and goes. */
  readonly playerName: string;
  /** A name rolled here, so the menu shows the same one on the way back. */
  readonly onRollName: (name: string) => void;
  /**
   * Who this machine is playing as, read off the profile by the shell.
   *
   * Announced once, on opening. A character is changed on the upgrade screen, which is a menu away
   * from here, so it cannot move while this screen is up.
   */
  readonly playerSheet: CharacterSheet;
  /** Where a line about somebody else goes. The shell owns the stack, so the game keeps it. */
  readonly onNotice?: ((notice: SessionNotice) => void) | undefined;
  /** Called on both sides once a game begins, with the connection it is played over. */
  readonly onStart?: (session: NetSession, modeId: string, lobby: LobbySession) => void;
  /**
   * An already-open session, rather than a fresh one.
   *
   * Two callers hand one in: the diagnostics panel, so both surfaces drive the same session, and
   * the shell when a guest comes back to this screen mid-game to reconnect, because the session is
   * the only thing that still knows which code it was in.
   */
  readonly session?: LobbySession | undefined;
}

export interface CoopLobby {
  dispose(): void;
}

export function createCoopLobby(container: HTMLElement, handlers: CoopLobbyHandlers): CoopLobby {
  const removeStyles = injectLobbyStyles();
  const session = handlers.session ?? createLobbySession();
  const intent = handlers.intent ?? null;

  const root = el('div', 'lobby');
  const status = el('p', 'lobby-status', LOBBY_DISCONNECTED);
  const advice = el('p', 'lobby-advice', '');
  const code = createLobbyCodePanel();
  const players = createLobbyPlayerList();
  // Rolling in the lobby renames a seat that already exists: the host renames its own, a guest asks
  // the host to. Either way the room hears about it in the next roster.
  const roller = createNameRoller({
    name: handlers.playerName,
    onRoll: (name: string): void => {
      session.setName(name);
      handlers.onRollName(name);
    },
  });
  session.setName(handlers.playerName);
  session.setSheet(handlers.playerSheet);
  const reconnect = createLobbyReconnectPanel(() => {
    session.cancelRejoin();
  });

  // The one rule a room chooses. A toggle for the host, a line of text for everybody else, because
  // the host owns the rules the same way it owns the world.
  const rulesRow = el('div', 'lobby-rules');
  const rulesLabel = el('span', 'lobby-rules-label', LOBBY_RULES_LABEL);
  const rulesButton = button(LOBBY_RULES_OFF, 'lobby-quiet lobby-rules-toggle');
  const rulesRead = el('span', 'lobby-rules-read', LOBBY_RULES_OFF);
  rulesRow.append(rulesLabel, rulesButton, rulesRead);

  const readyButton = button(LOBBY_READY, 'lobby-action lobby-ready');
  const startButton = button(LOBBY_START, 'lobby-action lobby-start');
  const startWarning = el('p', 'lobby-warning', '');
  const backButton = button(LOBBY_BACK, 'lobby-quiet');

  const exits = el('div', 'lobby-exits');
  exits.append(readyButton, startButton, backButton);

  root.append(
    el('h1', 'lobby-title', LOBBY_TITLE),
    el('p', 'lobby-strap', LOBBY_STRAP),
    status,
    advice,
    reconnect.root,
    code.root,
    roller.root,
    players.root,
    rulesRow,
    startWarning,
    exits,
  );
  container.append(root);

  // Once a game takes the connection over, leaving the screen must not close it underneath them.
  let handedOff = false;
  // Declared before the first render, which runs the moment this screen subscribes.
  let leaving = false;
  // True from the moment a reconnect is asked for until it has succeeded or run out of budget.
  // Until then the session having failed is the thing being waited on, not a reason to give up.
  let waitingToGetBack = intent?.kind === 'rejoin';
  let ready = false;
  // Who the warning was shown about, once it has been shown. The press answers "these players are
  // not ready", so the answer holds until that changes: a candidate resolving or a channel opening
  // redraws the lobby without moving a single seat, and used to make the second press unreachable.
  let warnedAbout: string | null = null;

  const begin = (facts: LobbyGameFacts, modeId: string): void => {
    const started = sessionFromLobby(session, facts);
    if (started === null || handlers.onStart === undefined) return;
    handedOff = true;
    handlers.onStart(started, modeId, session);
  };

  startButton.addEventListener('click', () => {
    const transport = session.transport();
    if (transport === null || intent?.kind !== 'host') return;

    // Said once, then honoured. A lobby one idle player can hold shut is worse than a game that
    // starts a moment early, and the host is the one who can see who is not ready.
    const waiting = notReady(session.state().roster);
    if (waiting.length > 0 && warnedAbout !== waiting.join(' ')) {
      warnedAbout = waiting.join(' ');
      setText(startWarning, lobbyNotReadyWarning(waiting.length));
      setVisible(startWarning, true);
      return;
    }

    // The join code is the seed. Both sides already have it, it is different every session, and
    // nothing has to invent a random number where no random number is allowed.
    begin(
      {
        role: 'host',
        seed: transport.joinCode,
        rules: session.state().rules,
        localPlayer: HOST_PLAYER_ID,
      },
      intent.mode.id,
    );
  });

  rulesButton.addEventListener('click', () => {
    session.setRules({ friendlyFire: !session.state().rules.friendlyFire });
  });

  readyButton.addEventListener('click', () => {
    ready = !ready;
    session.setReady(ready);
    setText(readyButton, ready ? LOBBY_NOT_READY : LOBBY_READY);
  });

  // A guest is taken into the game by the host rather than starting itself.
  const stopRelay = session.relayTo({
    ...silentListeners(),
    onMessage: (_from, frame): void => {
      if (frame.kind !== MESSAGE_KINDS.sessionStart) return;
      const start = decodeStart(frame.payload);
      // The rules come off the lobby message rather than off the start frame: the host announces
      // them the moment they change and again on every arrival, so by the time a game begins this
      // guest has already been told, and sending them twice would be two places to disagree.
      begin(
        {
          role: 'guest',
          seed: start.seed,
          rules: session.state().rules,
          localPlayer: start.playerId,
        },
        start.modeId,
      );
    },
  });

  backButton.addEventListener('click', () => {
    void session.leave().then(
      () => {
        handlers.onExit();
      },
      () => {
        handlers.onExit();
      },
    );
  });

  const unsubscribe = session.subscribe((state: LobbyState) => {
    render(state);
  });

  // Somebody joining or dropping is worth saying in the lobby too, not only once a game is running:
  // a row appearing and disappearing on its own is not the same as being told who went.
  const relayNotice = handlers.onNotice;
  const stopNotices =
    relayNotice === undefined ? (): void => undefined : session.onNotice(relayNotice);

  // Development only, and the same session either way: the panel observes this lobby rather than
  // opening a second one.
  const panel = attachNetPanel(container, session);

  if (intent?.kind === 'host') void session.host();
  if (intent?.kind === 'join') void session.join(intent.code);
  // Already in a session, with a seat being kept: no code to type and nothing to choose.
  if (intent?.kind === 'rejoin') {
    void session.rejoin().then(
      () => {
        waitingToGetBack = false;
        render(session.state());
      },
      () => {
        waitingToGetBack = false;
        render(session.state());
      },
    );
  }

  function render(state: LobbyState): void {
    const live = state.code !== '';
    const hosting = intent?.kind === 'host';

    setText(status, state.status.detail);
    setText(advice, state.status.code === null ? '' : (netErrorAdvice(state.status.code) ?? ''));
    setVisible(advice, advice.textContent !== '');

    code.show(state.code);
    setVisible(code.root, live && hosting);
    reconnect.show(state.rejoiningMsLeft);
    players.show(state.roster, state.localPlayer);

    // Only the host starts, and only into a mode there is one of. Only a guest is ever waited on.
    const friendlyFire = state.rules.friendlyFire;
    setText(rulesButton, friendlyFire ? LOBBY_RULES_ON : LOBBY_RULES_OFF);
    setText(rulesRead, friendlyFire ? LOBBY_RULES_ON : LOBBY_RULES_OFF);
    setVisible(rulesRow, live);
    setVisible(rulesButton, hosting);
    setVisible(rulesRead, !hosting);

    setVisible(startButton, live && hosting);
    setVisible(readyButton, live && !hosting && state.rejoiningMsLeft === null);
    // Still answering the same question, or not answering anything any more.
    if (warnedAbout !== notReady(state.roster).join(' ')) warnedAbout = null;
    setVisible(startWarning, warnedAbout !== null);

    // A session that ended is not one anybody can act on from here, unless getting back into it is
    // exactly what this screen is currently doing.
    if (waitingToGetBack) return;
    if (state.ending !== null || state.status.code === 'hostLeft') leaveFor('hostLeft');
  }

  /** Back to the menu, with the words for why. Guarded, because both signals can arrive. */
  function leaveFor(reasonCode: 'hostLeft'): void {
    if (leaving || handedOff) return;
    leaving = true;
    // Deferred, because the first render runs while this screen is still being constructed and the
    // shell cannot take down a lobby it has not been handed yet.
    queueMicrotask(() => {
      handlers.onExit({
        code: reasonCode,
        message: netErrorMessage(reasonCode),
        advice: netErrorAdvice(reasonCode) ?? '',
      });
    });
  }

  return {
    dispose(): void {
      roller.dispose();
      panel.dispose();
      unsubscribe();
      stopNotices();
      stopRelay();
      // A game that took the connection over owns it now, and closing it here would end the
      // session the moment the lobby screen came down.
      if (!handedOff) void session.leave();
      root.remove();
      removeStyles();
    },
  };
}

/** Which players the host is being asked to start without. Empty when the room is ready. */
function notReady(roster: readonly RosterSeat[]): readonly number[] {
  return roster.filter((seat) => !seat.host && !seat.ready).map((seat) => seat.playerId);
}

function button(text: string, className: string): HTMLButtonElement {
  const element = el('button', className, text);
  element.type = 'button';
  return element;
}
